import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapPin, Download, Copy, Check, Lock, ChevronLeft, ChevronRight } from 'lucide-react';
import { mulehunterService } from '../../services/api';
import { useFeature } from '../../auth/features';
import type { PhoneInventory, PhoneInventoryStatePage, PhoneInventoryStateTableRow } from '../../types/phoneInventory';

const PAGE_LIMIT = 100;

const SOURCE_TILES: { key: 'telegram' | 'p2p' | 'web_harvest' | 'authority' | 'other'; label: string }[] = [
    { key: 'telegram', label: 'Telegram' },
    { key: 'p2p', label: 'P2P exchanges' },
    { key: 'web_harvest', label: 'Web harvest' },
    { key: 'authority', label: 'Authority lists' },
    { key: 'other', label: 'Other' },
];

const fmtDate = (v: string | null | undefined) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN');
};

/** CSV cell: neutralise spreadsheet formulas, then quote. */
export const csvCell = (value: unknown): string => {
    let s = value === null || value === undefined ? '' : String(value);
    if (/^[=+\-@]/.test(s)) s = `'${s}`;
    return `"${s.replace(/"/g, '""')}"`;
};

const initialState = (): string | null => {
    try {
        return new URLSearchParams(window.location.search).get('state');
    } catch {
        return null;
    }
};

export const PhonesByStateTab: React.FC = () => {
    const allowed = useFeature('mulehunter.feed');
    const [inventory, setInventory] = useState<PhoneInventory | null>(null);
    const [invError, setInvError] = useState('');
    const [invLoading, setInvLoading] = useState(true);
    const [selected, setSelected] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [stateData, setStateData] = useState<PhoneInventoryStatePage | null>(null);
    const [rowsLoading, setRowsLoading] = useState(false);
    const [rowsError, setRowsError] = useState('');
    const [copied, setCopied] = useState<string | null>(null);

    useEffect(() => {
        if (!allowed) return;
        let cancelled = false;
        setInvLoading(true);
        setInvError('');
        mulehunterService.getPhoneInventory()
            .then(res => {
                if (cancelled) return;
                setInventory(res);
                const list = (res.by_state || []).filter(s => s.phones_either > 0);
                const wanted = initialState();
                const match = wanted && list.find(s => s.state.toLowerCase() === wanted.toLowerCase());
                const raj = list.find(s => s.state.toLowerCase() === 'rajasthan');
                setSelected((match || raj || list[0])?.state ?? null);
            })
            .catch(() => { if (!cancelled) setInvError('Phone inventory is unavailable. Counts have not been confirmed.'); })
            .finally(() => { if (!cancelled) setInvLoading(false); });
        return () => { cancelled = true; };
    }, [allowed]);

    useEffect(() => {
        if (!allowed || !selected) { setStateData(null); return; }
        let cancelled = false;
        setRowsLoading(true);
        setRowsError('');
        mulehunterService.getPhoneInventoryState(selected, { page, limit: PAGE_LIMIT })
            .then(res => { if (!cancelled) setStateData(res); })
            .catch(() => { if (!cancelled) { setStateData(null); setRowsError('Phone list for this state is unavailable.'); } })
            .finally(() => { if (!cancelled) setRowsLoading(false); });
        return () => { cancelled = true; };
    }, [allowed, selected, page]);

    const states = useMemo(() => (inventory?.by_state || []).filter(s => s.phones_either > 0), [inventory]);
    const current = states.find(s => s.state === selected) || null;
    const rows: PhoneInventoryStateTableRow[] = stateData?.rows || [];
    const totalPages = stateData ? Math.max(1, Math.ceil(stateData.total / (stateData.limit || PAGE_LIMIT))) : 1;

    const handleCopy = useCallback((phone: string) => {
        try { void navigator.clipboard.writeText(phone); } catch { /* clipboard unavailable */ }
        setCopied(phone);
        setTimeout(() => setCopied(null), 2000);
    }, []);

    const circleName = useCallback((code: string | null) => {
        if (!code) return null;
        return inventory?.by_circle?.find(c => c.circle_code === code)?.circle || code;
    }, [inventory]);

    const exportCsv = () => {
        if (!rows.length) return;
        const headers = ['Phone', 'Basis', 'Circle', 'Operator', 'Sources', 'Platforms', 'Channels', 'First seen', 'Last seen'];
        const lines = rows.map(r => [
            r.phone,
            r.basis.map(b => (b === 'series' ? 'DoT series' : 'Address record')).join('; '),
            circleName(r.circle_code) || '',
            r.operator || '',
            r.sources.join('; '),
            r.platforms.join('; '),
            r.channels.join('; '),
            r.first_seen || '',
            r.last_seen || '',
        ].map(csvCell).join(','));
        const blob = new Blob([[headers.map(csvCell).join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `phones_by_state_${(selected || 'all').toLowerCase().replace(/\s+/g, '_')}_page${page}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    if (!allowed) {
        return (
            <div className="flex items-start gap-3 rounded-xl border border-border bg-card/60 p-4 text-sm text-muted-foreground">
                <Lock className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <p>Phones by State is not included in your access tier. Request access from your administrator.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-5">
            <div className="rounded-xl border border-border bg-card/60 p-4">
                <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-emerald-500" aria-hidden />
                    <h2 className="text-base font-semibold text-foreground">Phones by State</h2>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                    Phones held across Telegram, P2P and harvest sources, bucketed by DoT telecom circle and address records.
                </p>
                {invLoading && <p className="mt-3 text-xs text-muted-foreground">Loading phone inventory...</p>}
                {invError && <p role="alert" className="mt-3 text-xs text-destructive">{invError}</p>}
                {inventory && (
                    <>
                        <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-6">
                            <div className="rounded-lg bg-muted/50 p-3">
                                <div className="text-xl font-semibold tabular-nums text-foreground">{inventory.retained_candidates.toLocaleString('en-IN')}</div>
                                <div className="text-[11px] text-muted-foreground">Distinct phones held</div>
                            </div>
                            {SOURCE_TILES.map(t => (
                                <div key={t.key} className="rounded-lg bg-muted/50 p-3">
                                    <div className="text-lg font-semibold tabular-nums text-foreground">
                                        {inventory.sources_grouped ? inventory.sources_grouped[t.key].toLocaleString('en-IN') : '—'}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground">{t.label}</div>
                                </div>
                            ))}
                        </div>
                        <p className="mt-2 text-[11px] text-muted-foreground">
                            A phone can appear in more than one source, so source tiles can sum to more than the total.
                            {inventory.attribution_note ? ` ${inventory.attribution_note}` : ''}
                        </p>
                    </>
                )}
            </div>

            {inventory && states.length === 0 && !invError && (
                <p className="text-sm text-muted-foreground">No phones are bucketed to a state yet.</p>
            )}

            {states.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-border bg-muted/40 p-1.5" aria-label="State">
                    {states.map(st => {
                        const active = st.state === selected;
                        return (
                            <button
                                key={st.state}
                                type="button"
                                aria-pressed={active}
                                onClick={() => { setSelected(st.state); setPage(1); }}
                                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all ${
                                    active ? 'bg-primary text-primary-foreground font-semibold shadow-sm'
                                        : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                                }`}
                            >
                                <span>{st.state}</span>
                                <span className={`rounded-full px-1.5 text-[10px] ${active ? 'bg-black/20 text-white' : 'bg-muted text-muted-foreground'}`}>
                                    {st.phones_either.toLocaleString('en-IN')}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {current && (
                <div className="rounded-xl border border-border bg-card p-4">
                    <h3 className="text-lg font-bold text-foreground">{current.state}</h3>
                    <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-3">
                        <div>
                            <div className="text-xl font-semibold tabular-nums text-foreground">{current.phones_by_series.toLocaleString('en-IN')}</div>
                            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">By DoT series</div>
                        </div>
                        <div>
                            <div className="text-xl font-semibold tabular-nums text-foreground">{current.phones_by_address.toLocaleString('en-IN')}</div>
                            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">By address record</div>
                        </div>
                        <div>
                            <div className="text-xl font-semibold tabular-nums text-foreground">{current.phones_either.toLocaleString('en-IN')}</div>
                            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Either basis</div>
                        </div>
                    </div>
                    <ul className="mt-3 space-y-1 text-[11px] text-muted-foreground">
                        <li><strong className="text-foreground">DoT series:</strong> the first four digits of the number were allocated by DoT to this telecom circle. Number portability can move it; this is not a location claim.</li>
                        <li><strong className="text-foreground">Address record:</strong> a historical cached address from a provider lookup names this state. It is not current residence and is unconfirmed.</li>
                    </ul>
                </div>
            )}

            {selected && (
                <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2">
                        <div className="text-xs text-muted-foreground">
                            {stateData ? (
                                <>
                                    <span className="font-semibold text-foreground">{stateData.total.toLocaleString('en-IN')}</span> phones
                                    {' '}(series {stateData.by_basis.series.toLocaleString('en-IN')}, address {stateData.by_basis.address.toLocaleString('en-IN')}, both {stateData.by_basis.both.toLocaleString('en-IN')})
                                </>
                            ) : 'Phones'}
                        </div>
                        <button
                            type="button"
                            onClick={exportCsv}
                            disabled={!rows.length}
                            className="flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
                        >
                            <Download className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                            Export this page (CSV)
                        </button>
                    </div>
                    {rowsError && <p role="alert" className="px-4 py-3 text-xs text-destructive">{rowsError}</p>}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead className="border-b border-border bg-muted/50 text-[11px] uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="px-4 py-3 font-medium">Phone</th>
                                    <th className="px-4 py-3 font-medium">Basis</th>
                                    <th className="px-4 py-3 font-medium">Operator</th>
                                    <th className="px-4 py-3 font-medium">Sources</th>
                                    <th className="px-4 py-3 font-medium">Platforms</th>
                                    <th className="px-4 py-3 font-medium">Channels</th>
                                    <th className="px-4 py-3 font-medium">First seen</th>
                                    <th className="px-4 py-3 font-medium">Last seen</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {rowsLoading ? (
                                    <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">Loading phones...</td></tr>
                                ) : rows.length === 0 ? (
                                    <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">No phones on this page.</td></tr>
                                ) : rows.map(r => (
                                    <tr key={r.phone} className="transition-colors hover:bg-muted/30">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1 font-mono font-medium text-foreground">
                                                <span>{r.phone}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleCopy(r.phone)}
                                                    title="Copy phone"
                                                    aria-label={`Copy ${r.phone}`}
                                                    className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                                >
                                                    {copied === r.phone ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {r.basis.includes('series') && (
                                                    <span className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-foreground">
                                                        DoT series{circleName(r.circle_code) ? `: ${circleName(r.circle_code)}` : ''}
                                                    </span>
                                                )}
                                                {r.basis.includes('address') && (
                                                    <span className="rounded border border-amber-500/30 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400">
                                                        Address record
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-foreground">{r.operator || '—'}</td>
                                        <td className="px-4 py-3 text-foreground">{r.sources.join(', ') || '—'}</td>
                                        <td className="px-4 py-3 text-foreground">{r.platforms.join(', ') || '—'}</td>
                                        <td className="px-4 py-3 text-foreground">{r.channels.join(', ') || '—'}</td>
                                        <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{fmtDate(r.first_seen)}</td>
                                        <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{fmtDate(r.last_seen)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-muted-foreground">
                        <button
                            type="button"
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page <= 1 || rowsLoading}
                            className="flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 hover:bg-muted disabled:opacity-50"
                        >
                            <ChevronLeft className="h-3.5 w-3.5" aria-hidden /> Prev
                        </button>
                        <span>Page {page} of {totalPages}</span>
                        <button
                            type="button"
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages || rowsLoading}
                            className="flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 hover:bg-muted disabled:opacity-50"
                        >
                            Next <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PhonesByStateTab;
